import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

export interface CreateAuditResp { auditId: string; uploadPath: string }
export const callCreateAudit = httpsCallable<{ orgId: string; displayName: string }, CreateAuditResp>(functions, 'createAudit');

export interface DeleteAuditReq { auditId: string }
export const callDeleteAudit = httpsCallable<DeleteAuditReq, { ok: true; auditId: string; deletedObjects: number }>(functions, 'deleteAudit');

export interface InviteUserReq { orgId: string; email: string; role: 'org_admin' | 'org_member' }
export interface InviteUserResp {
  inviteId: string;
  uid: string;
  created: boolean;
  /** True when Firebase Auth SMTP sent the password-setup email. */
  passwordEmailSent: boolean;
}
export const callInviteUser = httpsCallable<InviteUserReq, InviteUserResp>(functions, 'inviteUser');

export interface CreateOrgReq { displayName: string; legalName?: string; primaryContactEmail: string; primaryContactName: string; }
export const callCreateOrg = httpsCallable<CreateOrgReq, { orgId: string }>(functions, 'createOrg');

export interface AcceptInviteReq { inviteId: string }
export const callAcceptInvite = httpsCallable<AcceptInviteReq, { orgId: string; role: string }>(functions, 'acceptInvite');

export interface GenerateExportReq {
  auditId: string;
  type: 'pdf_executive' | 'pdf_full' | 'excel' | 'zip_bundle';
  /** Optional override; backend falls back to user profile locale, then FR. */
  locale?: 'fr' | 'en';
}
export const callGenerateExport = httpsCallable<GenerateExportReq, { exportId: string; signedUrl: string }>(functions, 'generateExport');

export interface SetExportPublishedReq { auditId: string; exportId: string; published: boolean }
export const callSetExportPublished = httpsCallable<SetExportPublishedReq, { ok: true; exportId: string; publishedToClient: boolean }>(functions, 'setExportPublished');

export interface SignedDownloadReq { kind: 'export' | 'release' | 'audit_zip' | 'document'; path: string }
export const callSignedDownload = httpsCallable<SignedDownloadReq, { url: string; expiresAt: number }>(functions, 'signedDownload');

export interface ReadAuditAssetReq { auditId: string; path: string }
export interface ReadAuditAssetResp { path: string; text: string }
/** Same-origin fallback to read a small json/ndjson audit asset when Storage CORS isn't configured. */
export const callReadAuditAsset = httpsCallable<ReadAuditAssetReq, ReadAuditAssetResp>(functions, 'readAuditAsset');

export interface PublishReleaseReq {
  version: string;
  channel: 'stable' | 'beta';
  os: 'windows' | 'macos' | 'linux';
  releaseNotes: string;
  sha256: string;
  sizeBytes: number;
  fileName: string;
}
export const callPublishRelease = httpsCallable<PublishReleaseReq, { releaseId: string; uploadPath: string }>(functions, 'publishRelease');

export interface AssumeOrgReq { orgId: string | null }
export const callAdminAssumeOrg = httpsCallable<AssumeOrgReq, { ok: true }>(functions, 'adminAssumeOrg');

export interface CreateUserDirectReq {
  orgId: string;
  email: string;
  displayName: string;
  role: 'org_admin' | 'org_member';
  tempPassword?: string;
  sendResetEmail?: boolean;
}
export interface CreateUserDirectResp {
  uid: string;
  email: string;
  tempPassword: string;
  passwordResetLink: string | null;
}
export const callCreateUserDirect = httpsCallable<CreateUserDirectReq, CreateUserDirectResp>(functions, 'createUserDirect');

export interface SetMemberRoleReq { orgId: string; uid: string; role: 'org_admin' | 'org_member' }
export const callSetMemberRole = httpsCallable<SetMemberRoleReq, { ok: true }>(functions, 'setMemberRole');

export interface RemoveMemberReq { orgId: string; uid: string }
export const callRemoveMember = httpsCallable<RemoveMemberReq, { ok: true; removedUid: string }>(functions, 'removeMember');

export interface RevokeInvitationReq { inviteId: string }
export const callRevokeInvitation = httpsCallable<RevokeInvitationReq, { ok: true }>(functions, 'revokeInvitation');

export interface GlobalAdmin {
  uid: string;
  email: string;
  displayName?: string | null;
  createdAt?: number | null;
  lastSignIn?: number | null;
}
export const callListGlobalAdmins = httpsCallable<void, { admins: GlobalAdmin[] }>(functions, 'listGlobalAdmins');

export interface PromoteGlobalReq { email: string }
export const callPromoteGlobalAdmin = httpsCallable<PromoteGlobalReq, { uid: string; email: string }>(functions, 'promoteGlobalAdmin');

export interface DemoteGlobalReq { uid: string }
export const callDemoteGlobalAdmin = httpsCallable<DemoteGlobalReq, { ok: true }>(functions, 'demoteGlobalAdmin');

export interface CreateGlobalAdminReq { email: string; displayName: string; tempPassword?: string }
export interface CreateGlobalAdminResp {
  uid: string;
  email: string;
  tempPassword: string | null;
  passwordResetLink: string | null;
  alreadyExisted: boolean;
}
export const callCreateGlobalAdminDirect = httpsCallable<CreateGlobalAdminReq, CreateGlobalAdminResp>(functions, 'createGlobalAdminDirect');

export interface ChatAssistantMessage { role: 'user' | 'model'; text: string }
export interface ChatAssistantReq { messages: ChatAssistantMessage[]; locale: 'fr' | 'en' }
export const callChatAssistant = httpsCallable<ChatAssistantReq, { reply: string }>(functions, 'chatAssistant');

// ── Toolkit entitlements ──────────────────────────────────────────────────────
export interface SetOrgToolkitsReq { orgId: string; toolkits: string[] }
export const callSetOrgToolkits = httpsCallable<SetOrgToolkitsReq, { ok: true }>(functions, 'setOrgToolkits');

// ── Generic org documents ─────────────────────────────────────────────────────
export interface CreateOrgDocumentReq { orgId: string; kind: string; title: string; description?: string }
export interface CreateOrgDocumentResp { docId: string; uploadPath: string }
export const callCreateOrgDocument = httpsCallable<CreateOrgDocumentReq, CreateOrgDocumentResp>(functions, 'createOrgDocument');

export interface FinalizeOrgDocumentReq { docId: string; fileName: string }
export const callFinalizeOrgDocument = httpsCallable<FinalizeOrgDocumentReq, { ok: true; docId: string; filePath: string }>(functions, 'finalizeOrgDocument');

export interface SetDocumentPublishedReq { docId: string; published: boolean }
export const callSetDocumentPublished = httpsCallable<SetDocumentPublishedReq, { ok: true; docId: string; publishedToClient: boolean }>(functions, 'setDocumentPublished');

export interface DeleteOrgDocumentReq { docId: string }
export const callDeleteOrgDocument = httpsCallable<DeleteOrgDocumentReq, { ok: true }>(functions, 'deleteOrgDocument');

// ── Support Tickets ───────────────────────────────────────────────────────────

export interface AttachmentMeta {
  contentType: string;
  sizeBytes: number;
  fileName: string;
}

export interface TicketUploadSlot { id: string; path: string }

export interface CreateTicketReq {
  orgId: string;
  subject: string;
  category: 'platform' | 'ai_system' | 'account' | 'other';
  description: string;
  attachmentMeta?: AttachmentMeta[];
}
export interface CreateTicketResp {
  ticketId: string;
  messageId: string;
  uploads: TicketUploadSlot[];
}
export const callCreateTicket = httpsCallable<CreateTicketReq, CreateTicketResp>(functions, 'createTicket');

export interface AddTicketMessageReq {
  ticketId: string;
  body: string;
  attachmentMeta?: AttachmentMeta[];
}
export interface AddTicketMessageResp {
  messageId: string;
  uploads: TicketUploadSlot[];
}
export const callAddTicketMessage = httpsCallable<AddTicketMessageReq, AddTicketMessageResp>(functions, 'addTicketMessage');

export interface UpdateTicketStatusReq {
  ticketId: string;
  status: 'open' | 'in_progress' | 'closed';
}
export const callUpdateTicketStatus = httpsCallable<UpdateTicketStatusReq, { ok: true }>(functions, 'updateTicketStatus');
